import { useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import {
  Text,
  Flex,
  DatePeriodPicker,
  PortfoliosTreeSelect,
  FilterContext,
  useScreenResolution,
  ColorContext,
} from 'dash-ui';

import { useGetPortfoliosTreeQuery } from 'gql/generated.graphql';

const PageWrapper = styled(Flex)`
  left: 0;
`;

function PortfolioViewsHeader() {
  const {
    devices: { isMobile },
  } = useScreenResolution();
  const { t } = useTranslation(['pages']);
  const { setFilterState } = useContext(FilterContext);
  const { data } = useGetPortfoliosTreeQuery({
    fetchPolicy: 'network-only',
  });
  const { setColorMap, getRandomColor, getDashColor } =
    useContext(ColorContext);

  const [selectedFoldersIds, setSelectedFoldersIds] = useState<string[]>([]);
  const [selectedValues, setSelectedValues] = useState<
    { itemId: string; parentFolderId?: string }[]
  >([]);

  const getOptions = (portfolio: any, treeIndex: number) => {
    const dashColor = getDashColor();
    const newColorMap: Record<string, string> = {};
    const color = Object.values(newColorMap).includes(dashColor)
      ? getRandomColor()
      : dashColor;
    newColorMap[portfolio._id] = color;

    return {
      value: portfolio._id as string,
      label: portfolio.name || portfolio?.property?.name || '' as string,
      treeIndex,
      color,
    };
  };

  const options = useMemo(() => {
    const portfolios = data?.getPortfoliosTree?.collection || [];

    return portfolios.map((item) => ({
      ...getOptions(item, 0),
      ...(item.items.length > 0 && {
        children: item.items?.map((child) => ({
          ...getOptions(child, 1),
          parentFolderId: item._id,
        })),
      }),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const mapOptionChildren = (option): { itemId: string; parentFolderId?: string }[] =>
    option.children.map(((o) => ({
      itemId: o.value,
      parentFolderId: o.parentFolderId,
    })));

  useEffect(() => {
    setColorMap(
      options.reduce((obj, item) => {
        if (item.children) {
          item.children.forEach((child) => {
            // @ts-ignore
            obj[child.value] = child.color;
          });
        }
        return { ...obj, [item.value]: item.color };
      }, {})
    );
    setSelectedFoldersIds((prev) => [
      ...prev,
      ...options.map(o => o.value),
    ]);

    setSelectedValues((prev) => [
      ...prev,
      // @ts-ignore
      ...options.filter(o => o.children).reduce((acc, o) => [...acc, ...mapOptionChildren(o)], []),
    ]);
  
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options]);

  return (
    <PageWrapper w="100%" pos="sticky">
      <Flex flex={1}>
        <Text lg semibold>
          {isMobile
            ? t('pages:dashboardPage:portfoliosWidget:titleShort')
            : t('pages:dashboardPage:portfoliosWidget:title')}
        </Text>
      </Flex>
      <Flex flex={1} justifyContent="flex-end" alignItems="center">
        <Flex mr={5.5}>
          <PortfoliosTreeSelect
            selectedValues={selectedValues}
            setSelectedValues={setSelectedValues}
            selectedFoldersIds={selectedFoldersIds}
            setSelectedFoldersIds={setSelectedFoldersIds}
            w={[76, 116, 116, 116]}
            disabled={!options.length}
            options={options}
            placeholder={
              isMobile
                ? t(
                    'pages:dashboardPage.portfoliosWidget.portfoliosDropdownInputShort'
                  )
                : t(
                    'pages:dashboardPage.portfoliosWidget.portfoliosDropdownInput'
                  )
            }
            handleSelectedValues={(values) => {
              setFilterState('portfolioIds', Array.from(new Set(values)) || []);
            }}
            menuPlacementHorizontal="right"
            // @ts-ignore
            isOptionDisabled={({ disabled }: { disabled: boolean }) => disabled}
          />
        </Flex>
        <Flex>
          <DatePeriodPicker
            placementHorizontal="left"
            customEnabled
            anchorIconProps={{ ml: 2, size: 16 }}
            onChange={(dateRange) => {
              setFilterState('dateRange', dateRange);
            }}
          />
        </Flex>
      </Flex>
    </PageWrapper>
  );
}

export default PortfolioViewsHeader;
